import { gql, useMutation } from '@apollo/client';
import styled from '@emotion/styled';
import { navigate } from '@reach/router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutationErrorHandler } from 'utils/mutation-utils';
import {
  AcceptGameInvitation,
  AcceptGameInvitationVariables,
  RejectGameInvitation,
  RejectGameInvitationVariables,
} from '../../../../../graphql/types';
import colors from '../../../../../lib/colors';
import { InvitationsState } from '../../../hooks/useInvitations';
import InvitationView from './InvitationView';

interface Props {
  invitations: InvitationsState;
}

const InvitationList = ({ invitations: { invitations, error } }: Props) => {
  const [t] = useTranslation();

  const handleError = useMutationErrorHandler();
  const [acceptInvitationFn] = useMutation<
    AcceptGameInvitation,
    AcceptGameInvitationVariables
  >(acceptInvitationMutation, { onError: handleError });
  const acceptInvitation = useCallback(
    async (id: string) => {
      const result = await acceptInvitationFn({ variables: { id } });
      if (result?.data == null) {
        return;
      }
      navigate(`/game/${result.data.acceptGameInvitation.id}`, {
        state: { pathname: window.location.pathname },
      });
    },
    [acceptInvitationFn],
  );

  const [rejectInvitationFn] = useMutation<
    RejectGameInvitation,
    RejectGameInvitationVariables
  >(rejectInvitationMutation, { onError: handleError });
  const rejectInvitation = useCallback(
    async (id: string) => {
      await rejectInvitationFn({ variables: { id } });
    },
    [rejectInvitationFn],
  );

  return (
    <>
      {error && (
        <ErrorView>
          {t('lobby.invitationsError', 'Nepodařilo se načíst pozvánky')}
        </ErrorView>
      )}
      {invitations != null && invitations.length > 0 && (
        <List>
          {invitations.map((invitation) => (
            <li key={invitation.id}>
              <InvitationView
                invitation={invitation}
                acceptInvitation={acceptInvitation}
                rejectInvitation={rejectInvitation}
              />
            </li>
          ))}
        </List>
      )}
    </>
  );
};

export default InvitationList;

const List = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

const ErrorView = styled.p`
  color: ${colors.danger};
  text-align: center;
`;

const acceptInvitationMutation = gql`
  mutation AcceptGameInvitation($id: ID!) {
    acceptGameInvitation(id: $id) {
      id
    }
  }
`;

const rejectInvitationMutation = gql`
  mutation RejectGameInvitation($id: ID!) {
    rejectGameInvitation(id: $id)
  }
`;
